import { AWSUser, User } from "@archetypes/auth";
import { Question, FormValue, ElementType } from "@archetypes/form";
import { getUniqueListBy } from "./generic.util";
import { FormResponse } from "@archetypes/responses";
import { TimeFormat } from "@archetypes/generic";
import { CURRENT_DATE, CURRENT_DATE_TIME, CURRENT_TIME, CURRENT_USER_SUB } from "@constants/global";

export const getCurrentDate = () => {
  return new Date().toISOString();
};

export const getCurrentTime = (timeFormat?: TimeFormat) => {
  return new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: timeFormat === TimeFormat.Twelve,
  });
};

export const getCurrentDateTime = () => new Date().toISOString();

export const getDefaultUsers = ({
  currentUser,
  isMultiChoice,
  defaultValue,
}: {
  currentUser: User;
  isMultiChoice?: boolean;
  defaultValue: FormValue;
}) => {
  if (isMultiChoice) {
    let defaultUsers = (defaultValue ?? []) as User[];
    defaultUsers = getUniqueListBy(
      defaultUsers.map((user) => {
        if (user?.sub === CURRENT_USER_SUB) {
          return currentUser;
        } else {
          return user;
        }
      }),
      "sub",
    );
    return defaultUsers;
  } else {
    let defaultUser = defaultValue as User;
    if (defaultUser?.sub === CURRENT_USER_SUB) {
      defaultUser = currentUser;
    }
    return [defaultUser];
  }
};

export const transformDefaultValue = ({
  question,
  users,
  loggedInUser,
  defaultValue,
}: {
  question: Question;
  users: User[];
  loggedInUser?: AWSUser;
  defaultValue?: FormValue;
}): FormValue => {
  if (!defaultValue) {
    return null;
  }

  if (question.type === ElementType.Users && users.length > 0 && loggedInUser) {
    return getDefaultUsers({
      isMultiChoice: question?.content?.user?.isMultiChoice,
      currentUser: users.find((user) => user?.sub === loggedInUser?.sub) as User,
      defaultValue,
    });
  } else if (question.type === ElementType.DatePicker) {
    if (defaultValue === CURRENT_DATE) {
      return getCurrentDate();
    }
    if (defaultValue === CURRENT_DATE_TIME) {
      return getCurrentDateTime();
    } else {
      return defaultValue as string;
    }
  } else if (question.type === ElementType.TimePicker) {
    if (defaultValue === CURRENT_TIME) {
      return getCurrentTime(question.content.timePicker?.timeFormat);
    } else {
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
};

export const getDefaultValuesForForm = ({
  questions,
  users,
  loggedInUser,
  defaultValues,
}: {
  questions: Question[];
  users: User[];
  loggedInUser?: AWSUser;
  defaultValues?: FormResponse;
}): FormResponse => {
  const transformedDefaultValues: FormResponse = {};
  questions.forEach((question) => {
    transformedDefaultValues[question.id] = transformDefaultValue({
      question,
      users,
      loggedInUser,
      defaultValue: defaultValues?.[question.id] ?? question.content.defaultValue,
    });
  });
  return transformedDefaultValues;
};
